import type {
  AbuseIpDbSnapshot,
  Ip2LocationSnapshot,
  IpFamily,
  IpinfoSnapshot,
  OsintBlocklistSnapshot,
  NetworkResult,
  ReputationResult,
  ScanResponse
} from "./contracts";
import type { RegistrationSnapshot } from "./contracts";

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function optionalString(value: unknown, maxLength = 256): string | undefined {
  return typeof value === "string" && value.trim() && value.length <= maxLength
    ? value.trim()
    : undefined;
}

function optionalBoolean(value: unknown): boolean | undefined {
  return typeof value === "boolean" ? value : undefined;
}

function optionalBoundedNumber(value: unknown, min: number, max: number): number | undefined {
  return typeof value === "number" && Number.isFinite(value) && value >= min && value <= max
    ? value
    : undefined;
}

export function isValidIPv4(value: string): boolean {
  const parts = value.split(".");
  return (
    parts.length === 4 &&
    parts.every((part) => /^(0|[1-9]\d{0,2})$/.test(part) && Number(part) >= 0 && Number(part) <= 255)
  );
}

export function normalizeIPv6(value: string): string | null {
  const candidate = value.trim();
  if (!candidate.includes(":") || candidate.includes("%")) return null;
  try {
    const hostname = new URL(`http://[${candidate}]/`).hostname;
    if (!hostname.startsWith("[") || !hostname.endsWith("]")) return null;
    return hostname.slice(1, -1).toLowerCase();
  } catch {
    return null;
  }
}

export function normalizeIp(value: string): { ip: string; family: IpFamily } | null {
  const candidate = value.trim();
  if (isValidIPv4(candidate)) return { ip: candidate, family: "IPv4" };
  const ipv6 = normalizeIPv6(candidate);
  return ipv6 ? { ip: ipv6, family: "IPv6" } : null;
}

export function isPublicIp(value: string): boolean {
  const normalized = normalizeIp(value);
  if (!normalized) return false;
  if (normalized.family === "IPv4") {
    const [a, b] = normalized.ip.split(".").map(Number);
    return !(
      a === 0 ||
      a === 10 ||
      (a === 100 && b >= 64 && b <= 127) || // RFC 6598 CGNAT
      a === 127 ||
      (a === 169 && b === 254) ||
      (a === 172 && b >= 16 && b <= 31) ||
      (a === 192 && b === 168) ||
      a >= 224
    );
  }
  const ip = normalized.ip;
  if (ip.startsWith("::ffff:")) {
    const parts = ip.split(":");
    const high = Number.parseInt(parts[parts.length - 2], 16);
    const low = Number.parseInt(parts[parts.length - 1], 16);
    return isPublicIp(`${high >> 8}.${high & 255}.${low >> 8}.${low & 255}`);
  }
  return !(
    ip === "::" ||
    ip === "::1" ||
    ip.startsWith("fe8") ||
    ip.startsWith("fe9") ||
    ip.startsWith("fea") ||
    ip.startsWith("feb") ||
    ip.startsWith("fc") ||
    ip.startsWith("fd")
  );
}

export function parseScanBody(
  value: unknown
): { valid: true; body: { turnstileToken?: string } } | { valid: false } {
  if (value === null || value === undefined) return { valid: true, body: {} };
  if (typeof value !== "object" || Array.isArray(value)) return { valid: false };
  const record = value as Record<string, unknown>;
  const keys = Object.keys(record);
  if (keys.some((key) => key !== "turnstileToken")) return { valid: false };
  if (
    record.turnstileToken !== undefined &&
    (typeof record.turnstileToken !== "string" || record.turnstileToken.length > 2048)
  )
    return { valid: false };
  return { valid: true, body: { turnstileToken: record.turnstileToken as string | undefined } };
}

export function parseIpinfoSnapshot(value: unknown): IpinfoSnapshot | null {
  const data = record(value);
  if (!data) return null;
  const snapshot: IpinfoSnapshot = {};
  const countryCode = optionalString(data.countryCode, 2);
  const asnNumber = optionalString(data.asnNumber, 32);
  const asnName = optionalString(data.asnName);
  if (countryCode && /^[A-Z]{2}$/.test(countryCode)) snapshot.countryCode = countryCode;
  if (asnNumber && /^AS[1-9]\d{0,9}$/.test(asnNumber)) snapshot.asnNumber = asnNumber;
  if (asnName) snapshot.asnName = asnName;
  return Object.keys(snapshot).length ? snapshot : null;
}

export function parseIp2LocationSnapshot(value: unknown): Ip2LocationSnapshot | null {
  const data = record(value);
  if (!data) return null;
  const snapshot: Ip2LocationSnapshot = {};
  const countryCode = optionalString(data.countryCode, 2);
  const regionName = optionalString(data.regionName);
  const cityName = optionalString(data.cityName);
  const asnName = optionalString(data.asnName);
  if (countryCode && /^[A-Z]{2}$/.test(countryCode)) snapshot.countryCode = countryCode;
  if (regionName) snapshot.regionName = regionName;
  if (cityName) snapshot.cityName = cityName;
  if (typeof data.isProxy === "boolean") snapshot.isProxy = data.isProxy;
  const asn = optionalString(data.asn, 32);
  if (asn && /^(AS)?[1-9]\d{0,9}$/.test(asn)) snapshot.asn = asn;
  if (asnName) snapshot.asnName = asnName;
  return Object.keys(snapshot).length ? snapshot : null;
}

export function parseOsintBlocklistSnapshot(value: unknown): OsintBlocklistSnapshot | null {
  const data = record(value);
  if (!data || data.source !== "abuse_ch_feodo" || typeof data.matched !== "boolean") return null;
  const entriesChecked = optionalBoundedNumber(data.entriesChecked, 0, 10_000);
  if (entriesChecked === undefined || !Number.isInteger(entriesChecked)) return null;
  const sourceUpdatedAt = optionalString(data.sourceUpdatedAt, 64);
  if (data.sourceUpdatedAt !== undefined && (!sourceUpdatedAt || !Number.isFinite(Date.parse(sourceUpdatedAt))))
    return null;
  return {
    source: "abuse_ch_feodo",
    matched: data.matched,
    entriesChecked,
    ...(sourceUpdatedAt ? { sourceUpdatedAt } : {})
  };
}

export function parseAbuseIpDbSnapshot(value: unknown): AbuseIpDbSnapshot | null {
  const data = record(value);
  if (!data) return null;
  const abuseConfidenceScore = optionalBoundedNumber(data.abuseConfidenceScore, 0, 100);
  const abuseReportCount = optionalBoundedNumber(data.abuseReportCount, 0, Number.MAX_SAFE_INTEGER);
  if (abuseConfidenceScore === undefined || abuseReportCount === undefined) return null;
  if (!Number.isSafeInteger(abuseReportCount)) return null;
  const lastReportedAt = optionalString(data.lastReportedAt, 64);
  return { abuseConfidenceScore, abuseReportCount, ...(lastReportedAt && Number.isFinite(Date.parse(lastReportedAt)) ? { lastReportedAt } : {}) };
}

export function parseReputationResult(value: unknown): ReputationResult | null {
  const data = record(value);
  if (
    !data ||
    (data.provider !== "scamalytics" &&
      data.provider !== "tor_project" &&
      data.provider !== "mock")
  )
    return null;
  if (!["low", "medium", "high", "unknown"].includes(String(data.riskBand))) return null;

  const result: ReputationResult = {
    provider: data.provider,
    riskBand: data.riskBand as ReputationResult["riskBand"]
  };
  const score = optionalBoundedNumber(data.score, 0, 100);
  const abuseConfidenceScore = optionalBoundedNumber(data.abuseConfidenceScore, 0, 100);
  const abuseReportCount = optionalBoundedNumber(data.abuseReportCount, 0, Number.MAX_SAFE_INTEGER);
  if (data.score !== undefined && score === undefined) return null;
  if (data.abuseConfidenceScore !== undefined && abuseConfidenceScore === undefined) return null;
  if (data.abuseReportCount !== undefined && abuseReportCount === undefined) return null;
  if (score !== undefined) result.score = score;
  if (abuseConfidenceScore !== undefined) result.abuseConfidenceScore = abuseConfidenceScore;
  if (abuseReportCount !== undefined) result.abuseReportCount = abuseReportCount;

  for (const key of ["proxy", "vpn", "tor", "datacenter"] as const) {
    const flag = optionalBoolean(data[key]);
    if (data[key] !== undefined && flag === undefined) return null;
    if (flag !== undefined) result[key] = flag;
  }
  if (
    data.torSource !== undefined &&
    data.torSource !== "scamalytics" &&
    data.torSource !== "tor_project"
  )
    return null;
  if (data.torSource !== undefined && result.tor !== true) return null;
  if (data.torSource !== undefined)
    result.torSource = data.torSource as ReputationResult["torSource"];
  const rawCategory = optionalString(data.rawCategory);
  const attributionUrl = optionalString(data.attributionUrl, 2048);
  const sourceObservedAt = optionalString(data.sourceObservedAt, 64);
  if (rawCategory) result.rawCategory = rawCategory;
  if (attributionUrl) result.attributionUrl = attributionUrl;
  if (sourceObservedAt && Number.isFinite(Date.parse(sourceObservedAt)))
    result.sourceObservedAt = sourceObservedAt;
  if (
    result.provider === "tor_project" &&
    (result.tor !== true ||
      result.torSource !== "tor_project" ||
      result.score !== undefined ||
      result.proxy !== undefined ||
      result.vpn !== undefined ||
      result.datacenter !== undefined)
  )
    return null;
  return result;
}

function parseNetworkResult(value: unknown): NetworkResult | null {
  const data = record(value);
  if (!data || typeof data.ip !== "string") return null;
  const normalized = normalizeIp(data.ip);
  if (!normalized || data.family !== normalized.family) return null;
  const result: NetworkResult = { ip: normalized.ip, family: normalized.family };
  const countryCode = optionalString(data.countryCode, 2);
  const region = optionalString(data.region);
  const city = optionalString(data.city);
  const timezone = optionalString(data.timezone, 128);
  const organization = optionalString(data.organization);
  const asn = optionalBoundedNumber(data.asn, 1, 4_294_967_295);
  if (countryCode && /^[A-Z]{2}$/.test(countryCode)) result.countryCode = countryCode;
  if (region) result.region = region;
  if (city) result.city = city;
  if (timezone) result.timezone = timezone;
  if (organization) result.organization = organization;
  if (asn !== undefined && Number.isInteger(asn)) result.asn = asn;
  for (const key of ["regionCode", "continent", "postalCode", "colo", "httpProtocol", "tlsVersion"] as const) {
    const text = optionalString(data[key], 64);
    if (text) result[key] = text;
  }
  for (const key of ["latitude", "longitude", "clientTcpRtt", "clientQuicRtt", "deliveryRate"] as const) {
    const min = key === "latitude" ? -90 : key === "longitude" ? -180 : 0;
    const max = key === "latitude" ? 90 : key === "longitude" ? 180 : Number.MAX_SAFE_INTEGER;
    const number = optionalBoundedNumber(data[key], min, max);
    if (number !== undefined) result[key] = number;
  }
  return result;
}

export function parseRegistration(value: unknown): RegistrationSnapshot | null {
  const data = record(value);
  if (!data || !["ARIN", "RIPE NCC", "APNIC", "LACNIC", "AFRINIC"].includes(String(data.rir))) return null;
  const start = typeof data.startAddress === "string" ? normalizeIp(data.startAddress) : null;
  const end = typeof data.endAddress === "string" ? normalizeIp(data.endAddress) : null;
  const fetchedAt = optionalString(data.fetchedAt, 64);
  if (!start || !end || start.family !== end.family || !fetchedAt || !Number.isFinite(Date.parse(fetchedAt))) return null;
  const result: RegistrationSnapshot = { rir: data.rir as RegistrationSnapshot["rir"], startAddress: start.ip, endAddress: end.ip, fetchedAt };
  for (const key of ["network", "organization"] as const) {
    const text = optionalString(data[key]);
    if (text) result[key] = text;
  }
  if (typeof data.countryCode === "string" && /^[A-Z]{2}$/.test(data.countryCode)) result.countryCode = data.countryCode;
  if (Array.isArray(data.cidrs)) {
    const cidrs = data.cidrs.filter((c): c is string => {
      if (typeof c !== "string" || c.length > 64) return false;
      const [address, width, extra] = c.split("/");
      const ip = normalizeIp(address); const length = Number(width);
      return !extra && !!ip && width !== undefined && /^\d{1,3}$/.test(width) && Number.isInteger(length) &&
        length >= 0 && length <= (ip.family === "IPv4" ? 32 : 128);
    });
    if (cidrs.length <= 32) result.cidrs = cidrs;
  }
  return result;
}

export function parseScanResponse(value: unknown): ScanResponse | null {
  const data = record(value);
  if (
    !data ||
    data.version !== "1" ||
    typeof data.requestId !== "string" ||
    !data.requestId ||
    typeof data.observedAt !== "string" ||
    !Number.isFinite(Date.parse(data.observedAt)) ||
    !Array.isArray(data.limitations) ||
    !data.limitations.every((item) => typeof item === "string" && item.length <= 128)
  )
    return null;

  const network = parseNetworkResult(data.network);
  if (!network) return null;
  const reputation = data.reputation === null ? null : parseReputationResult(data.reputation);
  if (data.reputation !== null && !reputation) return null;

  return {
    version: "1",
    requestId: data.requestId,
    observedAt: data.observedAt,
    network,
    registration: parseRegistration(data.registration),
    reputation,
    abuseipdb: data.abuseipdb == null ? null : parseAbuseIpDbSnapshot(data.abuseipdb),
    ipinfo: data.ipinfo == null ? null : parseIpinfoSnapshot(data.ipinfo),
    ip2location: data.ip2location == null ? null : parseIp2LocationSnapshot(data.ip2location),
    osintBlocklist: data.osintBlocklist == null
      ? null
      : parseOsintBlocklistSnapshot(data.osintBlocklist),
    limitations: [...data.limitations]
  };
}
